/**
 * Géométrie du réordonnancement par glisser-déposer.
 *
 * Ce module est **pur** : il ne connaît ni le DOM, ni React, ni les articles.
 * Il ne fait que répondre à deux questions, à partir d'un relevé de positions
 * pris une seule fois au début du geste :
 *
 *   1. à quel rang l'élément déplacé doit-il atterrir ? (`targetIndex`)
 *   2. de combien chaque autre ligne doit-elle glisser pour montrer le trou ?
 *      (`shiftFor`)
 *
 * C'est là que se jouait le comportement erratique observé : les hauteurs de
 * ligne sont **variables** (un article peut porter une ligne « Ajouté par… »,
 * une quantité, un libellé sur deux lignes). Un calcul qui suppose des lignes
 * de hauteur égale produit exactement les symptômes constatés — lignes qui se
 * chevauchent, trou béant au milieu de la liste, article qui atterrit à côté
 * de l'endroit visé.
 *
 * Le relevé est fait **une fois**, au `pointerdown`, et n'est plus jamais
 * remesuré pendant le geste : mesurer pendant qu'on déplace revient à mesurer
 * une règle avec elle-même, et c'est la deuxième source classique de
 * scintillement.
 *
 * S'y ajoute `planItemMove`, qui traduit un rang d'affichage en voisins de
 * position. Elle est ici, et non dans le store, pour être testable sans
 * IndexedDB ni DOM — c'est la fonction qui décide où l'article atterrit.
 */

import type { HouseholdState } from '@shared/merge.ts';
import { visibleItems } from '@shared/merge.ts';
import type { Item } from '@shared/types.ts';

/** Position d'une ligne dans le repère du **contenu** défilant, en pixels. */
export interface RowMetric {
  /** Bord supérieur, mesuré depuis le haut du contenu (défilement inclus). */
  top: number;
  height: number;
}

/**
 * Bord supérieur qu'aurait la ligne déplacée si elle atterrissait au rang `k`.
 *
 * C'est le pivot de tout le calcul. On raisonne sur la liste **telle qu'elle
 * sera** une fois la ligne retirée de son emplacement : les lignes situées
 * après elle remontent de sa hauteur, celles d'avant ne bougent pas. La suite
 * est strictement croissante, ce qui donne gratuitement un rang monotone.
 */
function slotTop(rows: readonly RowMetric[], from: number, k: number): number {
  const row = rows[k] as RowMetric;
  const moved = rows[from] as RowMetric;

  // Avant l'emplacement d'origine, la ligne prendrait la place de `row`.
  // Après, elle se glisserait derrière `row` une fois celle-ci remontée.
  return k <= from ? row.top : row.top + row.height - moved.height;
}

/**
 * Rang d'atterrissage de la ligne `from` déplacée de `delta` pixels : celui
 * dont l'emplacement est le plus proche du bord supérieur atteint.
 *
 * Comparer des **bords** et non des centres est ce qui rend le calcul juste
 * avec des hauteurs inégales. Avec des centres, une ligne plus haute que la
 * première de la liste ne pouvait jamais atteindre le premier rang : poussée à
 * fond vers le haut, son centre restait sous celui de la première ligne, et
 * elle atterrissait obstinément en deuxième position.
 *
 * Deux propriétés en découlent, et ce sont elles qui rendent le geste lisible :
 *
 *   - à `delta = 0`, l'emplacement d'origine est à distance nulle : rien ne
 *     bouge tant que le doigt n'a pas franchi la moitié d'un intervalle ;
 *   - aux bornes rendues par `deltaBounds`, le bord atteint coïncide
 *     exactement avec le premier ou le dernier emplacement, qui sont donc
 *     toujours atteignables.
 */
export function targetIndex(rows: readonly RowMetric[], from: number, delta: number): number {
  const moved = rows[from];
  if (!moved) return from;

  const top = moved.top + delta;
  let target = from;
  // Amorcé sur l'emplacement d'origine : à égalité, on ne bouge pas.
  let best = Math.abs(top - slotTop(rows, from, from));

  for (let k = 0; k < rows.length; k++) {
    const distance = Math.abs(top - slotTop(rows, from, k));
    if (distance < best) {
      best = distance;
      target = k;
    }
  }

  return target;
}

/**
 * Décalage vertical à appliquer à la ligne `index` pendant le geste.
 *
 * Toutes les lignes traversées glissent d'exactement la hauteur de la ligne
 * déplacée — ni plus, ni moins — puisque c'est précisément la place qu'elle
 * libère. Aucune autre ligne ne bouge, ce qui exclut par construction les
 * chevauchements et les trous.
 */
export function shiftFor(index: number, from: number, target: number, height: number): number {
  if (index === from) return 0;
  if (target > from) return index > from && index <= target ? -height : 0;
  if (target < from) return index >= target && index < from ? height : 0;
  return 0;
}

/**
 * Contraint un rang aux bornes d'un groupe.
 *
 * Les articles cochés sont affichés en bas de liste quoi qu'il arrive : laisser
 * un article non coché atterrir visuellement au milieu d'eux montrerait un
 * résultat que l'affichage annulerait aussitôt. On borne donc le geste au
 * groupe d'origine.
 */
export function clampToGroup(target: number, first: number, last: number): number {
  return Math.max(first, Math.min(target, last));
}

/**
 * Amplitude autorisée du déplacement, en pixels.
 *
 * Borner le **rang** ne suffit pas : sans borner aussi le déplacement visuel,
 * la ligne continue de suivre le doigt bien au-delà de son groupe — elle
 * survole des lignes qu'elle ne peut pas dépasser, tandis que la place qu'elle
 * a libérée reste béante à l'autre bout. C'est exactement l'impression de
 * désordre qu'on cherche à supprimer.
 *
 * En bornant le déplacement à la bande occupée par le groupe, la ligne
 * s'arrête visiblement là où elle ne peut plus aller. Le geste devient
 * auto-explicatif : ce qu'on voit est ce qu'on obtiendra.
 */
export function deltaBounds(
  rows: readonly RowMetric[],
  from: number,
  first: number,
  last: number,
): { min: number; max: number } {
  const moved = rows[from];
  const top = rows[first];
  const bottom = rows[last];
  if (!moved || !top || !bottom) return { min: 0, max: 0 };

  return {
    min: Math.min(0, top.top - moved.top),
    max: Math.max(0, bottom.top + bottom.height - (moved.top + moved.height)),
  };
}

/** Ramène une valeur dans un intervalle. */
export function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(value, max));
}

/**
 * Voisins de l'article une fois déposé au rang `targetIndex` de la liste
 * affichée, ou `null` si l'ordre ne change pas — auquel cas il n'y a rien à
 * écrire, et surtout rien à faire remonter dans le delta des deux appareils.
 *
 * Les voisins sont cherchés dans le **même groupe** (coché / non coché).
 * `visibleItems` fait systématiquement basculer les articles cochés en bas de
 * liste : une position calculée à cheval sur les deux groupes serait donc
 * annulée par l'affichage, et l'article reviendrait à sa place sous les yeux
 * de l'utilisateur. En bornant au groupe, déposer un article non coché « après
 * les cochés » le place à la fin des non cochés — ce qui est ce qu'on voulait.
 */
export function planItemMove(
  view: HouseholdState,
  item: Item,
  targetIndex: number,
): { before: Item | null; after: Item | null } | null {
  const visible = visibleItems(view, item.list_id);
  const from = visible.findIndex((i) => i.id === item.id);
  if (from === -1) return null;

  const reordered = [...visible];
  reordered.splice(from, 1);
  reordered.splice(Math.max(0, Math.min(targetIndex, reordered.length)), 0, item);

  const at = reordered.indexOf(item);
  if (at === from) return null;

  const sameGroup = (other: Item) => other.is_checked === item.is_checked;

  let before: Item | null = null;
  for (let i = at - 1; i >= 0; i--) {
    const candidate = reordered[i] as Item;
    if (sameGroup(candidate)) {
      before = candidate;
      break;
    }
  }

  let after: Item | null = null;
  for (let i = at + 1; i < reordered.length; i++) {
    const candidate = reordered[i] as Item;
    if (sameGroup(candidate)) {
      after = candidate;
      break;
    }
  }

  return { before, after };
}
